import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Check, QrCode, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSelectTheme } from "@/contexts/SelectThemeContext";
import { useCart } from "@/contexts/CartContext";

// Handles both Venmo and Cash App — identical UI, differing only by handle/provider name.
// Reads `isSelect` from the SelectThemeContext (PaymentInstructions.jsx sets it
// based on `order.cart_tier`). Applies the same R-1617 dark palette used by
// Cart + Checkout so the whole post-checkout arc stays on-brand.
export default function ManualPayment({ order }) {
  const [handles, setHandles] = useState(null);
  const [qr, setQr] = useState(null);
  const [copied, setCopied] = useState("");
  const { isSelect } = useSelectTheme();
  const { clear } = useCart();

  useEffect(() => {
    (async () => {
      const [h, q] = await Promise.all([
        api.get("/payment-info"),
        api.get(`/orders/${order.id}/payment-qr`),
      ]);
      setHandles(h.data);
      setQr(q.data);
    })();
  }, [order.id]);

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(""), 2000);
  };

  const shell = isSelect ? "bg-[#F1E5C7] text-[#1B1614] min-h-screen" : "";
  const cardCls = isSelect
    ? "bg-[#EBE0BE] rounded-2xl border border-[#D6C8A3] p-8"
    : "bg-white rounded-2xl border border-borderSubtle p-8";
  const eyebrow = isSelect
    ? "text-[11px] uppercase tracking-[0.22em] text-[#1B1614]/60 mb-4"
    : "text-[11px] uppercase tracking-[0.22em] text-stone mb-4";
  const muted = isSelect ? "text-[#1B1614]/70" : "text-stone";
  const strong = isSelect ? "text-[#1B1614]" : "text-charcoal";
  const primaryBtn = isSelect
    ? "bg-[#DDA94E] text-[#1B1614] hover:bg-[#F1E5C7]"
    : "bg-charcoal text-bone hover:bg-oxblood";
  const iconBtn = isSelect
    ? "p-2 border border-[#D6C8A3] hover:border-[#DDA94E] hover:text-[#6E521A] text-[#1B1614]/70 transition-colors rounded-full"
    : "p-2 border border-borderSubtle hover:border-charcoal hover:text-charcoal transition-colors rounded-full";
  const hairline = isSelect ? "border-t border-[#D6C8A3]" : "border-t border-borderSubtle";

  if (!handles || !qr) {
    return (
      <div className={shell}>
        <div className={`p-12 overline ${muted}`}>Loading order…</div>
      </div>
    );
  }

  const isVenmo = order.payment_method === "venmo";
  const handle = isVenmo ? handles.venmo : handles.cashapp;
  const providerName = isVenmo ? "Venmo" : "Cash App";
  const handleLabel = isVenmo ? "handle" : "$cashtag";

  return (
    <div className={shell}>
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12 py-14 lg:py-20" data-testid="payment-page">
        <p className={eyebrow}>Step 3 of 3 · Awaiting payment</p>
        <h1 className={`font-serif text-5xl md:text-6xl tracking-tight mb-4 ${strong}`}>Send payment.</h1>

        {/* Success banner */}
        <div
          className={`rounded-xl px-5 py-4 mb-6 flex items-center gap-3 ${
            isSelect
              ? "bg-[#DDA94E]/[0.08] border border-[#DDA94E]/40"
              : "bg-[#EDF7EE] border border-[#B7DCB9]"
          }`}
          data-testid="order-placed-banner"
        >
          <Check size={20} strokeWidth={2.5} className={`shrink-0 ${isSelect ? "text-[#6E521A]" : "text-[#2F7A31]"}`} />
          <div className="text-sm">
            <p className={`font-semibold ${isSelect ? "text-[#1B1614]" : "text-[#265427]"}`}>Order placed · #{order.order_number}</p>
            <p className={isSelect ? "text-[#1B1614]/80" : "text-[#2F7A31]"}>A confirmation email is on its way to <span className="font-mono">{order.customer_email}</span>.</p>
          </div>
        </div>

        <p className={`text-sm mb-12 max-w-2xl ${muted}`}>
          Order <span className={`font-mono ${strong}`}>#{order.order_number}</span> is reserved.
          Scan the QR to open {providerName} on your phone, or use the handle below to pay manually. Once we receive it, we'll ship immediately.
        </p>

        <div className="grid md:grid-cols-[auto_1fr] gap-8 lg:gap-16">
          {/* QR code card */}
          <div className={`${cardCls} flex flex-col items-center`} data-testid="qr-card">
            <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] mb-4 ${muted}`}>
              <Smartphone size={13} strokeWidth={1.5}/> Scan to pay
            </div>
            <img
              src={`data:image/png;base64,${qr.qr_png_base64}`}
              alt={`Pay ${providerName} QR code`}
              className="w-56 h-56 rounded-xl"
              data-testid="payment-qr-img"
            />
            <p className={`mt-5 font-serif text-2xl ${strong}`}>${order.total.toFixed(2)}</p>
            <p className={`text-[11px] uppercase tracking-[0.22em] ${muted}`}>via {providerName}</p>
            <a
              href={qr.deep_link}
              target="_blank"
              rel="noopener noreferrer"
              className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.22em] transition-colors ${primaryBtn}`}
              data-testid="open-app-btn"
            >
              <QrCode size={13} strokeWidth={2}/> Open {providerName}
            </a>
          </div>

          {/* Manual instructions */}
          <div>
            <div className={`${cardCls} lg:p-10`}>
              <p className={`text-[11px] uppercase tracking-[0.22em] mb-6 ${muted}`}>Or pay manually</p>

              <p className={`text-[11px] uppercase tracking-[0.22em] mb-2 ${muted}`}>Send exactly</p>
              <p className={`font-mono text-4xl mb-6 ${strong}`} data-testid="payment-amount">${order.total.toFixed(2)}</p>

              <p className={`text-[11px] uppercase tracking-[0.22em] mb-2 ${muted}`}>To this {providerName} {handleLabel}</p>
              <div className="flex items-center gap-3 mb-6">
                <p className={`font-serif text-2xl ${strong}`} data-testid="payment-handle">{handle}</p>
                <button
                  onClick={() => copy(handle, providerName + " handle")}
                  className={iconBtn}
                  data-testid="copy-handle-btn"
                  aria-label="Copy handle"
                >
                  {copied === providerName + " handle" ? <Check size={14}/> : <Copy size={14}/>}
                </button>
              </div>

              <p className={`text-[11px] uppercase tracking-[0.22em] mb-2 ${muted}`}>Include this note</p>
              <div className="flex items-center gap-3 mb-6">
                <p
                  className={`font-mono text-sm px-3 py-2 rounded-full ${
                    isSelect ? "bg-[#F1E5C7] border border-[#D6C8A3] text-[#1B1614]" : "bg-bone border border-borderSubtle"
                  }`}
                  data-testid="payment-note"
                >
                  Order {order.order_number}
                </p>
                <button
                  onClick={() => copy(`Order ${order.order_number}`, "Note")}
                  className={iconBtn}
                  data-testid="copy-note-btn"
                  aria-label="Copy note"
                >
                  {copied === "Note" ? <Check size={14}/> : <Copy size={14}/>}
                </button>
              </div>

              <div className={`${hairline} my-6`} />

              {/* Order recap */}
              <p className={`text-[11px] uppercase tracking-[0.22em] mb-3 ${muted}`}>Order recap</p>
              {order.items.map((i, idx) => (
                <div key={idx} className="flex justify-between mb-2 text-sm">
                  <span className={muted}>{i.name}{order.cart_tier === "supply" && i.unit_label ? ` · ${i.unit_label}` : ""} × {i.quantity}</span>
                  <span className={`font-mono ${strong}`}>${(i.price * i.quantity).toFixed(2)}</span>
                </div>
              ))}
              <div className={`${hairline} my-3`} />
              <div className="flex justify-between mb-1 text-xs">
                <span className={muted}>Subtotal</span>
                <span className={`font-mono ${strong}`}>${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between mb-2 text-xs">
                <span className={muted}>Shipping</span>
                <span className={`font-mono ${strong}`}>${(order.shipping ?? 22).toFixed(2)}</span>
              </div>
              <p className={`text-xs mt-3 ${muted}`}>Shipping to <span className={strong}>{order.address?.city}, {order.address?.state}</span></p>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Link to="/orders"
                onClick={() => { try { clear(); } catch { /* best-effort */ } }}
                className={`flex-1 py-4 tracking-[0.22em] text-xs uppercase text-center rounded-full transition-colors ${primaryBtn}`}
                data-testid="view-orders-btn">
            I've sent payment — view my orders
          </Link>
          <Link to="/shop"
                className={`flex-1 py-4 tracking-[0.22em] text-xs uppercase text-center rounded-full transition-colors border ${
                  isSelect
                    ? "border-[#DDA94E]/50 text-[#1B1614] hover:bg-[#DDA94E] hover:text-[#1B1614]"
                    : "border-charcoal text-charcoal hover:bg-charcoal hover:text-bone"
                }`}
                data-testid="continue-shopping-btn">
            Continue browsing
          </Link>
        </div>
      </div>
    </div>
  );
}
